import React, { useState, useImperativeHandle, forwardRef } from 'react';
import { useSelector } from 'react-redux';
import i18n from 'i18next';
import { notQualifiedTypes } from 'config/notQualifiedTypes';
import { loginTypes } from 'config/loginTypes';

import useLogout from 'client/hooks/logout/useLogout';
import useClientErrorHandler from 'client/hooks/useClientErrorHandler';

import DialogSystem from 'client/components/DialogSystem';
import PayPointCover from 'client/components/dialogVerification/popupPayPoint/PayPointCover';
import DialogEnterPasscode from 'client/components/dialogVerification/DialogEnterPasscode';
import DialogDeductPoint from 'client/components/dialogVerification/dialogDeductPoint/DialogDeductPoint';
import KKboxUpgradeMemberLevel from 'client/components/dialogLoginNoQualified/KKboxUpgradeMemberLevel';
import DialogLineAddFriend from 'client/components/dialogLoginNoQualified/DialogLineAddFriend';
import DialogBindCyberbizECMember from 'client/components/dialogLoginNoQualified/DialogBindCyberbizECMember';
import DialogEnterpriseBindLine from 'client/components/dialogLoginNoQualified/DialogEnterpriseBindLine';
import { gameTypes, hasPassLoginCheckTriggerGame } from 'config/promoGameTypes';
import DialogReferralCode from 'components/common/Dialog/DialogReferralCode';
import useSearchParams from 'hooks/useSearchParams';
import { getErrorTypeDialogDesc, errorTypes } from 'config/passcode';

const DialogQualifyCheckTrigger = forwardRef((_, ref) => {
    const logout = useLogout();
    const errHandler = useClientErrorHandler();
    const { loginMethod } = useSelector(s => s.settingPromo);
    const { isTestMode, hasUsablePasscode } = useSelector(s => s.promoState);
    const { notQualifiedType } = useSelector(s => s.userLoginState);
    const { userGameResultRecords: gameResultRecords = [] } = useSelector(s => s);
    const { type } = useSelector(s => s.promo);

    const {
        currentEntryUUID,
        isNeedReducePoint,
        createEntryNotQualifiedType,
        isNeedEnterPasscode
    } = useSelector(s => s.userEntryState);

    const { qualifyWithdrawUuid = '' } = useSelector(state => state.settingPointSystems);
    const notQualified = Boolean(notQualifiedType) || Boolean(createEntryNotQualifiedType);
    const [open, setOpen] = useState(false);
    const searchParams = useSearchParams();

    let hasInfo = false;
    useImperativeHandle(ref, () => ({
        checkIsHasInfo: shouldOpen => {
            if (shouldOpen && hasInfo) {
                setOpen(true);
            }
            return hasInfo;
        }
    }));

    function onClose() {
        setOpen(false);
    }
    function handleApiError(err) {
        errHandler({
            err
        });
    }

    if (notQualified) {
        hasInfo = true;
        if (notQualifiedType === notQualifiedTypes.ENTERPRISE.MEMBER_KKBOX) {
            return open && <KKboxUpgradeMemberLevel logout={logout} />;
        } else if (notQualifiedType === notQualifiedTypes.ENTERPRISE.CYBERBIZ_EC_MEMBER) {
            return <DialogBindCyberbizECMember open={open} onClose={onClose} />;
        } else if (notQualifiedType === notQualifiedTypes.LINE.FRIEND) {
            return <DialogLineAddFriend open={open} onClose={onClose} />;
        } else if (notQualifiedType === notQualifiedTypes.LINE.ENTERPRISE_MEMBER_BIND) {
            return <DialogEnterpriseBindLine open={open} onClose={onClose} />;
        } else if (
            createEntryNotQualifiedType === notQualifiedTypes.QUALIFICATION.MEMBER_ORDER ||
            notQualifiedType === notQualifiedTypes.QUALIFICATION.NAME_LIST_API
        ) {
            const title = i18n.t('label.no_qualify_join');
            const body = i18n.t('desc.sorry_no_qualify.name_list');
            const endButton = i18n.t('btn.understood');
            return (
                <DialogSystem
                    isOpen={open}
                    title={title}
                    body={body}
                    endButton={endButton}
                    closePopup={onClose}
                />
            );
        } else if (
            isTestMode &&
            createEntryNotQualifiedType === notQualifiedTypes.ENTRIES.REACH_LIMIT
        ) {
            const title = i18n.t('label.test_mode');
            const body = i18n.t('desc.testmode_credit_full');
            const endButton = i18n.t('btn.close');
            return (
                <DialogSystem
                    isOpen={open}
                    title={title}
                    body={body}
                    endButton={endButton}
                    closePopup={onClose}
                />
            );
        } else if (notQualifiedType === notQualifiedTypes.QUALIFICATION.NEW_REGISTER_MEMBER) {
            // 若有 gameResult 一樣可以直接進入查看，但不應該建立 entry、submit game result
            if (gameResultRecords.length > 0) return;
            const title = i18n.t('label.new_registration_member_fail');
            const body = i18n.t('desc.new_registration_member_fail');
            return <DialogSystem isOpen={open} title={title} body={body} endButton={false} />;
        } else if (notQualifiedType === notQualifiedTypes.QUALIFICATION.SUBMIT_REFERRAL_CODE) {
            // 新客加入獎勵有自己的邀請碼輸入頁面
            if (type === gameTypes.SIGN_UP) return null;

            return open ? <DialogReferralCode onClose={onClose} /> : null;
        } else {
            const title = i18n.t('label.no_qualify_join');
            const body = i18n.t('desc.sorry_no_qualify');
            return <DialogSystem isOpen={open} title={title} body={body} closePopup={onClose} />;
        }
    } else if (!hasUsablePasscode && isNeedEnterPasscode) {
        hasInfo = true;
        const hasPcodeInUrl = searchParams.has('pcode');
        if (hasPcodeInUrl) {
            const title = i18n.t('label.passcode.inuse');
            const body = getErrorTypeDialogDesc(errorTypes.USAGE_LIMIT_EXCEEDED);
            return <DialogSystem title={title} body={body} closePopup={onClose} isOpen={open} />;
        }
        return <DialogEnterPasscode open={open} onClose={onClose} onApiError={handleApiError} />;
    } else if (isNeedReducePoint) {
        if (Boolean(qualifyWithdrawUuid)) {
            hasInfo = true;
            return <DialogDeductPoint open={open} onClose={onClose} onApiError={handleApiError} />;
        }
        hasInfo = true;
        return <PayPointCover open={open} onClose={onClose} onPaypointError={handleApiError} />;
    } else if (
        !currentEntryUUID &&
        loginMethod !== loginTypes.INSTAGRAM &&
        !hasPassLoginCheckTriggerGame(type)
    ) {
        hasInfo = true;
        const title = i18n.t('label.promo_not_start');
        const body = i18n.t('label.try_again_later');
        return <DialogSystem title={title} body={body} closePopup={onClose} isOpen={open} />;
    }

    return null;
});

DialogQualifyCheckTrigger.displayName = 'DialogQualifyCheckTrigger';

export default DialogQualifyCheckTrigger;
